import { useEffect, useRef, useState, memo, useCallback, MutableRefObject } from 'react'
import styled, { keyframes } from 'styled-components'
import TableView, { ColumnConfig, FetchParams } from '../../components/controls/table-view'
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { updateStatus, approveOrder, fetchApprovals, fetchOrderSuppliers, fetchProductSuppliers, getCommonNotes, resetApprovals, updateApproval, UpdateOrderLinePayload, fetchApprovalTotals } from '../../store/features/ordering/approval/approval-slice';
import { ProductContainer, ProductImage, ProductNameContainer, ProductTitle } from '../../styles/shared/product';
import Image from '../../components/controls/image';
import Button from '../../components/controls/button';
import TextInput from '../../components/controls/text-input';
import { TextContainer, TableContainer } from '../../styles/shared/table';
import { setCurrentPage } from '../../store/features/authentication/user-slice';
import { GoogleIcon, ICON_ID, ICON_SIZES } from '../../components/icons/google-icon';
import { useNavigate, useParams } from 'react-router-dom';
import { ApplicationApprovalStatus } from '../../models/order/application-approval-status';
import { ApplicationSupplierProductHeader } from '../../models/supplier/application-supplier-product-header';
import { generateStatusIcon } from '../../helpers/status-helper';
import { fetchBranches } from '../../store/features/organisation/branch-slice';
import { openDrawer } from '../../store/features/drawer/drawer-slice';
import { DrawerID } from '../../constants/drawer-constants';
import Dropdown from '../../components/controls/dropdown';
import { SelectChangeEvent } from '@mui/material/Select/Select';
import Checkbox from '../../components/controls/checkbox';
import { mapStringToEnum } from '../../helpers/enum-helper';
import { currencyFormatter } from '../../helpers/formatters';
import { ApplicationSort } from '../../models/shared/application-sort';
import { ApplicationApprovalFilter } from '../../models/order/approval/application-approval-filter';
import NumberInput from '../../components/controls/number-input';
import LoadingSpinner from '../../components/controls/loading-spinner';
import PalletBreakdown from '../../components/order/pallet-breakdown';

export type SupplierOption = { value: string; label: string, size: number, costPrice: number | null }
type ProductSupplierState = {
    [productId: number]: {
        loaded: boolean;
        loading: boolean;
        options: SupplierOption[];
    };
};

interface SupplierSelectionListProps {
    productId: number;
    currentSupplierId: string | undefined;
    row: any;
    supplierInfo: ProductSupplierState[number] | undefined;
    optimisticSupplierId: string | undefined;
    pendingSupplierUpdateRef: MutableRefObject<Record<number, string>>;
    onSelect: (productId: number, supplierId: string, rowId: number) => void;
}

const SupplierSelectionList = memo(function SupplierSelectionList({
    productId,
    currentSupplierId,
    row,
    supplierInfo,
    optimisticSupplierId,
    pendingSupplierUpdateRef,
    onSelect,
}: SupplierSelectionListProps) {
    const options = supplierInfo?.options;
    const isLoading = supplierInfo?.loading ?? false;
    const selectedSupplierId = optimisticSupplierId ?? currentSupplierId;

    if (isLoading || !supplierInfo) {
        return <TextContainer>Loading...</TextContainer>;
    }

    if (!options?.length) {
        return <TextContainer>No suppliers available</TextContainer>;
    }

    return (
        <SupplierOptionsList>
            {options.map((option) => {
                const isActive = option.value === selectedSupplierId;
                const isUpdatingOption = pendingSupplierUpdateRef.current[row.id] === option.value;
                const hasPendingUpdate = pendingSupplierUpdateRef.current[row.id] != null;
                return (
                    <SupplierOptionItem
                        key={option.value}
                        $active={isActive}
                        disabled={isActive || hasPendingUpdate}
                        onClick={() => {
                            if (isActive || hasPendingUpdate) return;
                            onSelect(productId, option.value, row.id);
                        }}
                    >
                        <div>
                            <SupplierName>{option.label}</SupplierName>
                            <SupplierMeta>{option.size} {row.sizeUnit}</SupplierMeta>
                        </div>
                        <SupplierMetaRight>
                            <SupplierPrice>{option.costPrice != null ? currencyFormatter.format(option.costPrice) : 'TBC'}</SupplierPrice>
                            {isUpdatingOption && <UpdatingSpinner />}
                        </SupplierMetaRight>
                    </SupplierOptionItem>
                );
            })}
        </SupplierOptionsList>
    );
});

export default function AdminApprovalLines() {
    const { branchId, orderId } = useParams();
    const approval = useAppSelector(state => state.approval);
    const branches = useAppSelector(state => state.branch).branches;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [supplierState, setSupplierState] = useState<ProductSupplierState>({});
    const [optimisticSupplierSelection, setOptimisticSupplierSelection] = useState<Record<number, string>>({});
    const [filters, setFilters] = useState<ApplicationApprovalFilter | undefined>(undefined);
    const pendingSupplierUpdateRef = useRef<Record<number, string>>({});
    const requestedSupplierProductIdsRef = useRef<Set<number>>(new Set());
    const notesDraftRef = useRef<Record<number, string>>({});
    const [notesSuggestions, setNotesSuggestions] = useState<Record<number, string[]>>({});

    const loadOptions = useCallback(async (productIds: number[]) => {
        if (!branchId) {
            return;
        }

        const idsToLoad = productIds.filter(
            (productId) => !requestedSupplierProductIdsRef.current.has(productId)
        );
        if (idsToLoad.length === 0) return;

        idsToLoad.forEach((productId) => requestedSupplierProductIdsRef.current.add(productId));

        setSupplierState((prev) => {
            const nextState = { ...prev };
            idsToLoad.forEach((productId) => {
                nextState[productId] = {
                    loaded: prev[productId]?.loaded ?? false,
                    loading: true,
                    options: prev[productId]?.options ?? []
                };
            });
            return nextState;
        });

        const result = await dispatch(fetchProductSuppliers({ branchId: Number(branchId), productIds: idsToLoad }));
        const isFulfilled = fetchProductSuppliers.fulfilled.match(result);
        const payload = result.payload as Record<number, ApplicationSupplierProductHeader[]> | ApplicationSupplierProductHeader[] | null;
        const supplierMap = Array.isArray(payload)
            ? { [idsToLoad[0]!]: payload } as Record<number, ApplicationSupplierProductHeader[]>
            : payload ?? {};

        setSupplierState((prev) => {
            const nextState = { ...prev };
            idsToLoad.forEach((productId) => {
                const suppliers = supplierMap[productId] ?? [];
                nextState[productId] = {
                    loaded: isFulfilled,
                    loading: false,
                    options: suppliers.map((supplier: ApplicationSupplierProductHeader) => ({
                        value: supplier.supplierId.toString(),
                        label: supplier.supplierName,
                        size: supplier.size,
                        costPrice: supplier.price
                    }))
                };

                if (!isFulfilled) {
                    requestedSupplierProductIdsRef.current.delete(productId);
                }
            });
            return nextState;
        });
    }, [branchId, dispatch]);

    useEffect(() => {
        requestedSupplierProductIdsRef.current.clear();
        setSupplierState({});
    }, [branchId, orderId]);

    useEffect(() => {
        const productIds = approval.approvals?.orderLines
            ? Array.from(new Set(approval.approvals.orderLines.map((line) => line.productId)))
            : [];

        if (productIds.length > 0) {
            loadOptions(productIds);
        }
    }, [approval.approvals?.orderLines, loadOptions]);

    const handleSupplierSelect = useCallback((productId: number, supplierId: string, rowId: number) => {
        setOptimisticSupplierSelection((prev) => ({
            ...prev,
            [rowId]: supplierId
        }));
        pendingSupplierUpdateRef.current[rowId] = supplierId;

        dispatch(updateApproval({
            id: rowId,
            field: "supplierId",
            value: Number(supplierId)
        })).then((action) => {
            if (updateApproval.rejected.match(action)) {
                setOptimisticSupplierSelection((prev) => {
                    const next = { ...prev };
                    delete next[rowId];
                    return next;
                });
            }
            delete pendingSupplierUpdateRef.current[rowId];
        }).catch(() => {
            delete pendingSupplierUpdateRef.current[rowId];
        });
    }, [dispatch]);


    useEffect(() => {
        if (!branches) {
            dispatch(fetchBranches());
        }
    }, [branches])

    useEffect(() => {
        const rows = approval.approvals?.orderLines ?? [];
        const draftValues = rows.reduce((acc, row) => {
            acc[row.id] = acc[row.id] ?? row.notes ?? "";
            return acc;
        }, {} as Record<number, string>);
        notesDraftRef.current = draftValues;
    }, [approval.approvals?.orderLines]);

    const fetchMoreData = async (fetchParams: FetchParams) => {
        await dispatch(fetchApprovals({ orderId: Number(orderId), fetchParams, filters }))
    }

    const isNotOrderedInLastDays = (row: any, days: number = 4): boolean => {
        if (!row.lastOrdered || !row.lastOrdered.date) {
            return true; // Never ordered - highlight it
        }
        const lastOrderedDate = new Date(row.lastOrdered.date);
        const now = new Date();
        const daysSinceOrdered = Math.floor((now.getTime() - lastOrderedDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysSinceOrdered > days;
    };

    const enhanceRowsWithHighlight = (rows: any[]): any[] => {
        return rows.map((row) => ({
            ...row,
            className: isNotOrderedInLastDays(row, 4) ? 'not-ordered-highlight' : ''
        }));
    };

    useEffect(() => {
        dispatch(setCurrentPage("Approvals"))
        dispatch(fetchOrderSuppliers({ orderId: Number(orderId) }))
        dispatch(fetchApprovalTotals(Number(orderId)))
        return () => {
            dispatch(resetApprovals());
        }
    }, [orderId])


    function updateValue(
        payload: UpdateOrderLinePayload
    ) {
        dispatch(updateApproval(payload));
    }

    const columnConfig: { [key: string]: ColumnConfig } = {
        id: {
            hidden: true
        },
        productId: {
            hidden: true,
        },
        productDescription: {
            hidden: true
        },
        productSize: {
            hidden: true
        },
        productImage: {
            hidden: true
        },
        size: {
            hidden: true
        },
        sizeUnit: {
            hidden: true,
        },
        branchId: {
            hidden: true,
        },
        className: {
            hidden:true
        },
        openPricing: {
            hidden: true
        },
        plu: {
            hidden: true
        },
        costPrice: {
            hidden: true
        },
        supplierId: {
            hidden: true
        },
        orderId: {
            hidden: true
        },
        lastOrdered: {
            hidden: true,
        },
        productName: {
            order: 1,
            name: "Product",
            sort: true,
            colspan: 3,
            render: (value: number, row: any, parentRow: any) => (
                <ProductContainer>
                    {parentRow && (
                        <>
                            <TextContainer>Split</TextContainer>
                            <GoogleIcon id={ICON_ID.Split} isSecondary />
                        </>
                    )}
                    <div className="approvalicon">
                        {generateStatusIcon(row.approvalStatus ?? parentRow.approvalStatus)}
                    </div>
                    <ProductImage>
                        <Image image={row.productImage ?? parentRow.productImage}></Image>
                    </ProductImage>
                    <ProductNameContainer>
                        <ProductTitle>{value ?? parentRow.productName}</ProductTitle>
                        <div>{row.size} {row.sizeUnit} - Plu: {row.plu ?? parentRow.plu}</div>
                        <div>
                            <strong>{row.openPricing ? "Price TBC" : <>{currencyFormatter.format(row.costPrice! / row.size)} per {row.sizeUnit}</>}</strong>
                        </div>
                        {parentRow == null && (
                            <LastOrderedRow>
                                {row.lastOrdered ? (
                                    <LastOrderedDetails>
                                        <div>Last ordered: {new Date(row.lastOrdered.date).toLocaleDateString("en-gb", { day: "numeric", month: "short", year: "numeric" })}</div>
                                        <div>Last Stock: {row.lastOrdered.currentStock}</div>
                                        <div>Quantity: {row.lastOrdered.quantity}</div>
                                        <div>Cost price: {currencyFormatter.format(row.lastOrdered.cost)}</div>
                                        <div>Unit cost price: {currencyFormatter.format(row.lastOrdered.unitCost)}</div>
                                    </LastOrderedDetails>
                                ) : (
                                    <span>Not previously ordered</span>
                                )}
                                <Button
                                    variant="text"
                                    id={`viewPriceHistory_${row.id}`}
                                    label=""
                                    icon={ICON_ID.History}
                                    fullWidth={false}
                                    onClick={() => {
                                        dispatch(
                                            openDrawer({
                                                id: DrawerID.PriceHistory,
                                                anchor: "right",
                                                data: {
                                                    headerText: `Price History - ${row.productName ?? parentRow?.productName}`,
                                                    productId: row.productId ?? parentRow?.productId,
                                                    branchId: branchId
                                                },
                                            }))
                                    }}
                                />
                            </LastOrderedRow>
                        )}

                    </ProductNameContainer>
                </ProductContainer>
            )
        },
        currentStockLevel: {
            order: 2,
            name: "Current stock",
            sort: true,
            render: (value: number, row: any) => (
                <TextContainer>
                    {value}
                </TextContainer>
            )
        },
        quantity: {
            order: 3,
            sort: true,
            colspan: 2,
            render: (value: number, row: any) =>
                row.approvalStatus === ApplicationApprovalStatus.AwaitingApproval ? (
              <NumberInput
                label="Quantity"
                id={`quantity-${row.id}`}
                value={value}
                step={1}
                min={0}
                onChange={(value) =>  updateValue(
                                {
                                    id: row.id,
                                    field: "quantity",
                                    value: value
                                })}
              />
                
                ) : (
                    <TextContainer className={value == 0 ? "red-highlight" : ""}>{value}</TextContainer>
                )
        },

        supplierName: {
            order: 4,
            colspan: 2,
            sort: true,
            name: "Supplier",
            render: (value: string, row: any) => {
                const currentSupplierId = row.supplierId?.toString();
                return row.approvalStatus === ApplicationApprovalStatus.AwaitingApproval ? (
                    <SupplierSelectionList
                        productId={row.productId}
                        currentSupplierId={currentSupplierId}
                        row={row}
                        supplierInfo={supplierState[row.productId]}
                        optimisticSupplierId={optimisticSupplierSelection[row.id]}
                        pendingSupplierUpdateRef={pendingSupplierUpdateRef}
                        onSelect={handleSupplierSelect}
                    />
                ) : (
                    <TextContainer>{`${value} - £${row.costPrice}`}</TextContainer>
                );
            }
        },
        notes: {
            order: 5,
            colspan: 2,
            render: (value: string, row: any) => {
                const draft = notesDraftRef.current[row.id] ?? value ?? "";
                const suggestions = notesSuggestions[row.id];

                return row.approvalStatus === ApplicationApprovalStatus.AwaitingApproval ? (
                    <div onClick={() => {
                        // fetch suggestions on first click for this row
                        if (!notesSuggestions[row.id] && row.productId) {
                            const supplierId = Number(row.supplierId) || 0;
                            dispatch(getCommonNotes({ productId: row.productId, supplierId })).then((resp) => {
                                const notes = (resp.payload as string[] | null) ?? [];
                                setNotesSuggestions((prev) => ({ ...prev, [row.id]: notes }));
                            }).catch(() => {});
                        }
                    }}>
                        <TextInput
                            id={`notes-${row.id}`}
                            label="Notes"
                            type="text"
                            value={draft}
                            onChange={(event) => {
                                const newText = event.target.value;
                                notesDraftRef.current[row.id] = newText;
                            }}
                            onBlur={() => {
                                const newText = notesDraftRef.current[row.id] ?? value ?? "";
                                if (newText !== (row.notes ?? "")) {
                                    updateValue({
                                        id: row.id,
                                        field: "notes",
                                        value: newText,
                                    });
                                }
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.currentTarget.blur();
                                }
                            }}
                            fullWidth={true}
                        />
                       {suggestions && suggestions.length > 0 && (
    <SuggestionList>
        {suggestions.map((note, idx) => (
            <SuggestionItem
                key={idx}
                onClick={(e) => {
                    e.stopPropagation();
                    notesDraftRef.current[row.id] = note;
                    updateValue({
                        id: row.id,
                        field: "notes",
                        value: note
                    });
                    setNotesSuggestions((prev) => ({ ...prev, [row.id]: [] }));

                }}
            >
                {note}
            </SuggestionItem>
        ))}
    </SuggestionList>
)}
                    </div>
                ) : (
                    <TextContainer>{value}</TextContainer>
                )
            }
        },
        children: {
            order: 6,
            name: "",
            render: (value: string, row: any) => (
                row.approvalStatus === ApplicationApprovalStatus.AwaitingApproval && (
                    <Button variant="outlined" id={row.Name} label='Split' isSecondary={true} fullWidth={false} onClick={() => {
                        dispatch(
                            openDrawer({
                                id: DrawerID.SplitOrderDrawer,
                                anchor: "right",
                                data: {
                                    headerText: `Split ${row.productName}`,
                                    orderLineId: row.id,
                                    branchId: branchId
                                },
                            }))
                    }} />
                )
            )
        },
        approvalStatus: {
            order: 7,
            name: "",
            render: (value: string, row: any) => (
                row.approvalStatus === ApplicationApprovalStatus.AwaitingApproval && (
                    <Button
                        variant="contained"
                        id={row.Name}
                        label='Approve'
                        isPrimary={true}
                        fullWidth={false}
                        onClick={() => updateValue(
                            {
                                id: row.id,
                                field: "approvalStatus",
                                value: ApplicationApprovalStatus.Approved
                            })
                        } />
                )
            )
        },
    };

    const renderActionButton = (row: any) => (
        <ButtonContainer>
            {row.approvalStatus === ApplicationApprovalStatus.AwaitingApproval ? (
                <Button
                    variant="outlined"
                    isDanger={true}
                    id={row.Name}
                    label="Reject"
                    onClick={() => updateValue(
                        {
                            id: row.id,
                            field: "approvalStatus",
                            value: ApplicationApprovalStatus.Rejected
                        })
                    }
                    fullWidth={false}
                />
            ) : (
                <Button
                    variant="outlined"
                    id={row.Name}
                    label="Undo"
                    onClick={() => updateValue(
                        {
                            id: row.id,
                            field: "approvalStatus",
                            value: ApplicationApprovalStatus.AwaitingApproval
                        })
                    }
                    fullWidth={false}
                />
            )}

        </ButtonContainer>
    );

    const renderFilterBar = (sortOrder: ApplicationSort, sortColumn: string | undefined) => (
        <>
            <Dropdown
                label="Supplier"
                id="SupplierFilter"
                fullWidth={false}
                value={filters?.supplierId?.toString() ?? "-1"}
                options={[
                    { value: "-1", label: "[All]" },
                    ...(approval.orderSuppliers?.map((x) => ({
                        value: x.id.toString(),
                        label: x.name
                    })) ?? [])
                ]}
                variant="outlined"
                onChange={(e: SelectChangeEvent<unknown>) => {
                    const newFilter = {
                        ...filters,
                        supplierId: e.target.value === "-1" ? undefined : Number(e.target.value),
                    }
                    setFilters(newFilter)
                    dispatch(fetchApprovals({
                        orderId: Number(orderId),
                        filters: newFilter,
                        fetchParams: {
                            sortOrder,
                            sortColumn,
                            reset: true,
                        }
                    }))
                }
                }
                autoWidth={false}
                multiple={false}
                native={false}
            />
            <CheckboxContainer>
                <Checkbox
                    label="Quantity more than 0"
                    checked={filters?.positiveQuantity ?? false}
                    onChange={(event) => {
                        const newFilter = {
                            ...filters,
                            zeroQuantity: false,
                            positiveQuantity: event.target.checked
                        }
                        setFilters(newFilter)
                        dispatch(fetchApprovals({
                            orderId: Number(orderId),
                            filters: newFilter,
                            fetchParams: {
                                sortOrder,
                                sortColumn,
                                reset: true,
                            }

                        }))
                    }}
                />

                <Checkbox
                    label="Quantity equal to 0"
                    checked={filters?.zeroQuantity ?? false}
                    onChange={(event) => {
                        const newFilter = {
                            ...filters,
                            positiveQuantity: false,
                            zeroQuantity: event.target.checked
                        }
                        setFilters(newFilter)
                        dispatch(fetchApprovals({
                            orderId: Number(orderId),
                            filters: newFilter,
                            fetchParams: {
                                sortOrder,
                                sortColumn,
                                reset: true,
                            }
                        })
                        )
                    }}
                />

            </CheckboxContainer>
            <Dropdown
                label="Approval Status"
                id="StatusFilter"
                fullWidth={false}
                value={filters?.approvalStatus?.toString() ?? "Unknown"}
                options={[
                    { value: "Unknown", label: "[All]" },
                    ...(Object.entries(ApplicationApprovalStatus).filter(x => x[0] !== ApplicationApprovalStatus.Unknown).map(([key, val]) => ({
                        value: val,
                        label: val.replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before capital letters
                    })) ?? [])
                ]}
                variant="outlined"
                onChange={(e: SelectChangeEvent<unknown>) => {
                    const newFilter = {
                        ...filters,
                        approvalStatus: mapStringToEnum(ApplicationApprovalStatus, e.target.value as string)
                    }
                    setFilters(newFilter)
                    dispatch(fetchApprovals({
                        orderId: Number(orderId),
                        filters: newFilter,
                        fetchParams: {
                            sortOrder,
                            sortColumn,
                            reset: true,
                        }
                    }))
                }
                }
                autoWidth={false}
                multiple={false}
                native={false}
            />
            <Button
                id="approveAll"
                label="Reset Filters"
                variant="outlined"
                icon={ICON_ID.Reset}
                onClick={() => dispatch(fetchApprovals({
                    orderId: Number(orderId),
                    filters: undefined,
                    fetchParams: {
                        sortOrder,
                        sortColumn,
                        reset: true,
                    }
                }))}
            />
        </>
    )

    const renderHeader = () => {
        const branch = branches?.find(x => x.id == Number(branchId))?.name;
        return approval.awaitingApprovalCount > 0 ? (
            <ToolbarContainer>
                <div>
                    Order: #{orderId}
                </div>
                <div>
                    <GoogleIcon id={ICON_ID.Domain} size={ICON_SIZES.Large} />
                    {branch}
                </div>

                <Button
                    id="approveAll"
                    label="Approve All Items"
                    variant="outlined"
                    icon={ICON_ID.Approval}
                    onClick={() => dispatch(updateStatus({ orderId: Number(orderId), status: ApplicationApprovalStatus.Approved }))}
                />

                {(filters?.positiveQuantity ||
                    filters?.zeroQuantity ||
                    filters?.supplierId) && (
                        <div>
                            <Button
                                id="approveAll"
                                label="Approve Filter Selection"
                                variant="outlined"
                                icon={ICON_ID.Selection}
                                onClick={() => dispatch(updateStatus({ orderId: Number(orderId), status: ApplicationApprovalStatus.Approved, filters }))}
                            />
                        </div>
                    )}
            </ToolbarContainer>
        ) : (
            <ToolbarContainer>
                <div>
                    Order: #{orderId}
                </div>
                <div>
                    <GoogleIcon id={ICON_ID.Domain} size={ICON_SIZES.Large} />
                    {branch}
                </div>
                <Button
                    id="completeOrder"
                    label="Complete Order"
                    variant="contained"
                    loading={approval.loading}
                    onClick={() => dispatch(approveOrder({ orderId: Number(orderId), navigate }))}
                    icon={ICON_ID.CheckCircle}
                />
                <Button
                    id="undoAll"
                    label="Undo All"
                    variant="outlined"
                    onClick={() => dispatch(updateStatus({ orderId: Number(orderId), status: ApplicationApprovalStatus.AwaitingApproval }))}
                    icon={ICON_ID.SwapHorizontal}
                />
            </ToolbarContainer>
        );
    };

    return (
        <ApprovalContainer>
            <NotOrderedHighlightStyles>
                <TotalsRow>
                    <TotalsCard>
                        <TotalsHeading>Order Totals</TotalsHeading>
                        <TotalsValues>
                            <TotalsValue>
                                <span>Items</span>
                                <strong>{approval.totals?.totalItemCount ?? 0}</strong>
                            </TotalsValue>
                            <TotalsValue>
                                <span>Total</span>
                                <strong>{currencyFormatter.format(approval.totals?.totalValue ?? 0)}</strong>
                            </TotalsValue>
                        </TotalsValues>
                        <PalletBreakdown suppliers={approval.totals?.suppliers ?? []} />
                    </TotalsCard>
                </TotalsRow>
                <TableContainer>
                    <TableView
                        id="ordersTable"
                        emptyText='No approvals '
                        fetchData={(params) => fetchMoreData(params)}
                        filterBar={renderFilterBar}
                        totalCount={approval.approvals?.count ?? 0}
                        data={enhanceRowsWithHighlight(approval?.approvals?.orderLines ?? [])}
                        columnConfig={columnConfig}
                        tableHeader={renderHeader}
                        actionButton={renderActionButton}
                        showHeaders={true}
                        search
                    />
                </TableContainer>
            </NotOrderedHighlightStyles>
        </ApprovalContainer>
    )
}

const ApprovalContainer = styled.div`
    height:100%;
`

const TotalsRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
`;

const TotalsCard = styled.div`
    width: 100%;
    max-width: 420px;
    background-color: white;
    border: 1px solid ${(props) => props.theme.application.border};
    padding: 16px;
`;

const TotalsHeading = styled.h2`
    margin: 0 0 12px;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const TotalsValues = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
`;

const TotalsValue = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    span {
        font-size: 0.85rem;
        color: #555;
    }

    strong {
        font-size: 1.15rem;
    }
`;

const CheckboxContainer = styled.div`
display:flex;
flex-direction:column
`
const ButtonContainer = styled.div`
    display:flex;
`

const SupplierOptionsList = styled.div`
    display:flex;
    flex-direction:column;
    gap: 4px;
    max-height: 200px;  
    overflow-y: auto;
`

const SupplierOptionItem = styled.button<{ $active: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border: 1px solid ${(props) => (props.$active ? props.theme.application.scheme.success : props.theme.application.border)};
    background: ${(props) => (props.$active ? props.theme.application.scheme.success : props.theme.application.table.cellBackground)};
    color: ${(props) => (props.$active ? props.theme.application.scheme.contrast : "inherit")};
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
    text-align: left;

    min-height: 42px;

    &:hover {
        background: ${(props) => props.theme.application.scheme.hover};
        border-color: ${(props) => props.theme.application.scheme.primary};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.application.scheme.primary};
        outline-offset: 2px;
    }

    &:disabled {
        cursor: default;
        opacity: 0.85;
    }
`

const SupplierName = styled.div`
    font-weight: 600;
    margin-bottom: 2px;
    font-size: 0.95rem;
`

const SupplierMeta = styled.div`
    font-size: 0.75rem;
    opacity: 0.8;
`

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`

const SupplierPrice = styled.div`
    font-weight: 600;
    white-space: nowrap;
`

const SupplierMetaRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const UpdatingSpinner = styled.div`
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-left-color: ${(props) => props.theme.application.scheme.contrast};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`

const CurrentSupplierRow = styled.div`
    font-size: 0.9rem;
    white-space: nowrap;
`

const SuggestionList = styled.div`
    display:flex;
    flex-direction:row;
    flex-wrap:wrap;
    gap:2px;
    margin-top:3px;
`;

const SuggestionItem = styled.button`
    text-align:left;
    padding:6px 8px;
    background: ${(props) => props.theme.application.scheme.contrast};
    border:1px solid ${(props) => props.theme.application.border};
    opacity:0.6;
    border-radius:10px;
    cursor:pointer;
    &:hover{ 
         background: ${(props) => props.theme.application.scheme.hover};
         opacity:1;
     }
`;

const LastOrderedRow = styled.div`
    display:flex;
    align-items:flex-start;
    gap:4px;
    font-size:0.9rem;
    color:${(props) => props.theme.application.scheme.neutral ?? "inherit"};

    .MuiButtonBase-root{
        min-width:0;
        padding:2px;
    }
`

const LastOrderedDetails = styled.div`
    display:flex;
    flex-direction:column;
    gap:2px;
`

const ToolbarContainer = styled.div`
    background: white;
    border-bottom:1px solid  ${(props) => props.theme.application.border};
    padding:10px;
    font-size:2rem;
    display:flex;
    gap:20px;
    flex-wrap:wrap;
    flex:1;
`

export const NotOrderedHighlightStyles = styled.div`
    .not-ordered-highlight {
        background-color: rgba(255, 0, 0, 0.1) !important;
        
        td {
            background-color: rgba(255, 0, 0, 0.1) !important;
        }
    }
`